import { lazy, memo, Suspense, useMemo } from "react";
import { useSiteContent } from "@/hooks/useSiteContent";
import type { HeroV2Config } from "@/types/heroBannerV2";

const HeroBannerV2 = lazy(() => import("@/components/HeroBannerV2"));

export { FONT_OPTIONS } from "./hero/types";

const HeroSkeleton = () => (
  <section
    className="relative w-full overflow-hidden"
    style={{ minHeight: "50vh", contain: "layout style", background: "hsl(var(--background))" }}
  >
    <div
      className="absolute inset-0"
      style={{
        background: "linear-gradient(90deg, transparent 0%, hsl(var(--muted) / 0.3) 50%, transparent 100%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
    <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
  </section>
);

const HeroBanner = memo(() => {
  const { t, loading } = useSiteContent();

  const v2Config = useMemo<HeroV2Config | null>(() => {
    const raw = t("hero_banner_v2_config", "");
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return parsed?.enabled ? parsed : null;
    } catch {
      return null;
    }
  }, [t]);

  if (loading && !v2Config) {
    return <HeroSkeleton />;
  }

  if (!v2Config) {
    return null;
  }

  return (
    <Suspense fallback={<HeroSkeleton />}>
      <HeroBannerV2 initialConfig={v2Config} />
    </Suspense>
  );
});

HeroBanner.displayName = "HeroBanner";

export default HeroBanner;